import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { eur } from "@/lib/api";
import { Card, Btn, Field, inputCls } from "@/components/common";
import { MONTHS } from "./helpers";
import { Plus, Trash2 } from "lucide-react";

const COST_FIELDS = [
  ["vaagali_monthly_eur", "Vaagali Inn (€/mese)"], ["ref_occupancy", "Occupazione di riferimento variabili (%)"],
  ["min_variable_pct", "Quota minima variabili (%)"],
  ["cancel_rate", "Tasso cancellazione manuale (%)"], ["tax_base_pct", "Imponibile tasse (% del lordo)"], ["tgst_pct", "Aliquota TGST (%)"],
  ["green_tax_usd", "Green Tax ($ notte/ospite)"], ["green_council_usd", "Green Tax Council ($ notte/ospite)"], ["usd_eur", "Cambio 1 USD in € (vuoto = automatico)"],
];

export function CostSettings({ costs, onSaved }) {
  const [c, setC] = useState(costs);
  useEffect(() => { setC(costs); }, [costs]);
  const save = async () => {
    try {
      const body = Object.fromEntries(COST_FIELDS.filter(([k]) => c[k] !== "" && c[k] != null).map(([k]) => [k, Number(c[k])]));
      await api.put("/forecast-settings", { ...body, opening_month: c.opening_month, pickup_from: c.pickup_from, closed_months: c.closed_months || [], cancel_auto: c.cancel_auto !== false, taxes_included: c.taxes_included !== false });
      toast.success("Impostazioni costi salvate");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || "Valori non validi"); }
  };
  return (
    <Card className="p-4" data-testid="pv-costs">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Costi (condivisi con Annuale)</div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {COST_FIELDS.map(([k, l]) => (
          <Field key={k} label={l}><input data-testid={`pv-${k}`} type="number" step="0.01" value={c[k] ?? ""} onChange={(e) => setC({ ...c, [k]: e.target.value })} className={inputCls} /></Field>
        ))}
        <Field label="Apertura struttura"><input data-testid="pv-opening" type="month" value={c.opening_month || ""} onChange={(e) => setC({ ...c, opening_month: e.target.value })} className={inputCls} /></Field>
        <Field label="Storico pickup valido dal"><input data-testid="pv-pickup-from" type="date" value={c.pickup_from || ""} onChange={(e) => setC({ ...c, pickup_from: e.target.value })} className={inputCls} /></Field>
        <label className="flex items-center gap-2 text-xs text-slate-600"><input data-testid="pv-cancel-auto" type="checkbox" checked={c.cancel_auto !== false} onChange={(e) => setC({ ...c, cancel_auto: e.target.checked })} /> Cancellazioni dallo storico (altrimenti valore manuale)</label>
        <label className="flex items-center gap-2 text-xs text-slate-600"><input data-testid="pv-taxes-included" type="checkbox" checked={c.taxes_included !== false} onChange={(e) => setC({ ...c, taxes_included: e.target.checked })} /> Prezzi tasse incluse (TGST scorporata 17/117)</label>
        <div className="flex items-end"><Btn data-testid="pv-costs-save" onClick={save}>Salva costi</Btn></div>
      </div>
      <div className="mt-3 text-xs text-slate-500">Mesi di chiusura annuale a Keyodhoo (ogni anno; riapribili dal singolo mese)</div>
      <div className="flex flex-wrap gap-1 mt-1">
        {MONTHS.map((l, i) => {
          const on = (c.closed_months || []).includes(i + 1);
          return <button key={l} data-testid={`pv-closed-${i + 1}`} onClick={() => setC({ ...c, closed_months: on ? c.closed_months.filter((x) => x !== i + 1) : [...(c.closed_months || []), i + 1] })}
            className={`text-xs px-2 py-1 rounded-md border transition-colors ${on ? "bg-rose-100 border-rose-300 text-rose-700 font-semibold" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>{l}</button>;
        })}
      </div>
      <CostItemsEditor costs={costs} onSaved={onSaved} />
    </Card>
  );
}

function CostItemsEditor({ costs, onSaved }) {
  const [items, setItems] = useState(costs.fixed_items || []);
  useEffect(() => { setItems(costs.fixed_items || []); }, [costs]);
  const set = (i, k, v) => setItems(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const add = () => setItems([...items, { id: `n${Date.now()}`, group: "Altri fissi", name: "", amount: 0, currency: "MVR", target: "spese_generali" }]);
  const save = async () => {
    try {
      await api.put("/forecast-items", { items: items.map((it) => ({ ...it, name: it.name.trim(), amount: Number(it.amount) || 0 })) });
      toast.success("Voci di spesa salvate");
      onSaved();
    } catch (e) { toast.error("Controlla le voci: nome obbligatorio, importo ≥ 0"); }
  };
  return (
    <div className="mt-4" data-testid="pv-items">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Voci di spesa mensili standard</span>
        <span className="text-xs text-slate-600">Fissi <b data-testid="pv-items-fixed">{eur(costs.fixed_monthly_eur)}</b> · Variabili <b data-testid="pv-items-var">{eur(costs.variable_monthly_eur)}</b></span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-[520px]">
          <tbody>
            {items.map((it, i) => (
              <tr key={it.id} className="border-b border-slate-100" data-testid={`pv-item-${it.id}`}>
                <td className="py-1 pr-2"><select data-testid={`pv-item-group-${it.id}`} value={it.group} onChange={(e) => set(i, "group", e.target.value)} className="px-1 py-1 rounded border border-slate-300">{GROUPS.map((g) => <option key={g}>{g}</option>)}</select></td>
                <td className="py-1 pr-2"><input data-testid={`pv-item-name-${it.id}`} value={it.name} onChange={(e) => set(i, "name", e.target.value)} className="w-full px-2 py-1 rounded border border-slate-300" /></td>
                <td className="py-1 pr-2"><input data-testid={`pv-item-amount-${it.id}`} type="number" value={it.amount} onChange={(e) => set(i, "amount", e.target.value)} className="w-24 text-right px-2 py-1 rounded border border-slate-300" /></td>
                <td className="py-1 pr-2"><select data-testid={`pv-item-currency-${it.id}`} value={it.currency} onChange={(e) => set(i, "currency", e.target.value)} className="px-1 py-1 rounded border border-slate-300">{["MVR", "USD", "EUR"].map((x) => <option key={x}>{x}</option>)}</select></td>
                <td className="py-1 pr-2"><select data-testid={`pv-item-target-${it.id}`} title="Dove si registra la spesa confermata" value={it.target || "spese_generali"} onChange={(e) => set(i, "target", e.target.value)} className="px-1 py-1 rounded border border-slate-300">{TARGETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></td>
                <td className="py-1"><button data-testid={`pv-item-del-${it.id}`} onClick={() => setItems(items.filter((_, j) => j !== i))} className="p-1 text-slate-400 hover:text-red-600"><Trash2 size={13} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between mt-2">
        <button data-testid="pv-item-add" onClick={add} className="text-xs font-semibold text-sky-700 hover:text-sky-900 inline-flex items-center gap-1"><Plus size={13} /> Aggiungi voce</button>
        <Btn data-testid="pv-items-save" onClick={save}>Salva voci</Btn>
      </div>
    </div>
  );
}

const GROUPS = ["Struttura", "Ristorante", "Escursioni", "Altri fissi", "Variabili"];
const TARGETS = [["bollette", "→ Bollette"], ["ristorante", "→ Ristorante"], ["spese_generali", "→ Spese generali"], ["lavori", "→ Lavori"], ["affitti", "in pagina Affitti"], ["staff", "in pagina Staff"]];

export function TargetSettings({ year, target, onSaved }) {
  const [t, setT] = useState(target);
  useEffect(() => { setT(target); }, [target]);
  const toggle = (m) => setT({ ...t, mesi_alta: t.mesi_alta.includes(m) ? t.mesi_alta.filter((x) => x !== m) : [...t.mesi_alta, m] });
  const save = async () => {
    try {
      await api.put(`/previsionale/${year}/target`, { amount: t.amount === "" || t.amount == null ? null : Number(t.amount), peso_alta: Number(t.peso_alta) || 1, mesi_alta: t.mesi_alta });
      toast.success("Obiettivo salvato");
      onSaved();
    } catch (e) { toast.error("Valori non validi"); }
  };
  return (
    <Card className="p-4" data-testid="pv-target">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Obiettivo {year} (condiviso con Annuale)</div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Obiettivo netto annuo (€)"><input data-testid="pv-target-amount" type="number" value={t.amount ?? ""} onChange={(e) => setT({ ...t, amount: e.target.value })} placeholder="es. 80000" className={inputCls} /></Field>
        <Field label="Peso alta stagione (×)"><input data-testid="pv-target-weight" type="number" step="0.1" value={t.peso_alta ?? ""} onChange={(e) => setT({ ...t, peso_alta: e.target.value })} className={inputCls} /></Field>
      </div>
      <div className="mt-3 text-xs text-slate-500">Mesi di alta stagione</div>
      <div className="flex flex-wrap gap-1 mt-1">
        {MONTHS.map((l, i) => (
          <button key={l} data-testid={`pv-alta-${i + 1}`} onClick={() => toggle(i + 1)}
            className={`text-xs px-2 py-1 rounded-md border transition-colors ${t.mesi_alta.includes(i + 1) ? "bg-orange-100 border-orange-300 text-orange-700 font-semibold" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>{l}</button>
        ))}
      </div>
      <div className="flex items-center justify-between mt-3">
        <span className="text-[11px] text-slate-400">{t.data_fissaggio ? `Fissato il ${t.data_fissaggio.slice(0, 10).split("-").reverse().join("/")}` : "Nessun obiettivo fissato"}</span>
        <Btn data-testid="pv-target-save" onClick={save}>Salva obiettivo</Btn>
      </div>
    </Card>
  );
}
